// Pantallas sin sesion que abren los correos del CRM: aceptar una invitacion y crear una contrasena nueva.
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';

import { aceptarInvitacion, cambiarClaveConEnlace, correoTieneCuenta, empresaDeInvitacion, validarEnlaceClave, type EmpresaInvitacion } from '../api/acceso';
import { setSession } from '../api/http';
import { Icon } from '../lib/icons';

function Tarjeta({ children }: { children: ReactNode }) {
  return (
    <div className="login">
      <div className="login-card">
        <div className="brand" style={{ padding: 0 }}>
          <span className="mark">
            <Icon name="wave" />
          </span>
          <span className="bt">
            <b>CRM XmarteK</b>
            <small>CRM + 3CX</small>
          </span>
        </div>
        {children}
      </div>
    </div>
  );
}

const irAlCrm = () => window.location.replace('/hoy');
const CLAVE_MIN = 8;

export function Invitacion() {
  const hash = decodeURIComponent(window.location.pathname.split('/')[2] ?? '');
  const q = new URLSearchParams(window.location.search);
  const tokenPersonal = q.get('inviteToken') ?? undefined;
  const [empresa, setEmpresa] = useState<EmpresaInvitacion | null | undefined>(undefined);
  const [v, setV] = useState({ email: q.get('email') ?? '', nombre: '', apellido: '', clave: '' });
  const [error, setError] = useState('');
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    // Quien abre la invitacion entra como la persona invitada, no con la sesion que hubiera en este navegador.
    setSession(null);
    empresaDeInvitacion(hash).then(setEmpresa, () => setEmpresa(null));
  }, [hash]);

  // Quien ya tiene usuario (en otra empresa) entra con su contraseña de siempre: no se le pide crear una ni su nombre.
  const [tieneCuenta, setTieneCuenta] = useState(false);
  useEffect(() => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v.email.trim())) return setTieneCuenta(false);
    const t = setTimeout(() => correoTieneCuenta(v.email).then(setTieneCuenta, () => setTieneCuenta(false)), 300);
    return () => clearTimeout(t);
  }, [v.email]);

  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    if (!tieneCuenta && v.clave.length < CLAVE_MIN) return setError(`La contraseña debe tener al menos ${CLAVE_MIN} caracteres.`);
    setError('');
    setEnviando(true);
    try {
      await aceptarInvitacion({ hash, tokenPersonal, email: v.email, clave: v.clave, nombre: v.nombre, apellido: v.apellido, empresa: empresa! });
      irAlCrm();
    } catch (err) {
      const m = (err as Error).message;
      setError(/password|contraseña/i.test(m) && /incorrect|wrong|invalid|coinciden/i.test(m) ? (tieneCuenta ? 'Esa no es tu contraseña de CRM XmarteK. Escríbela a mano (no la que sugiere el navegador).' : 'Ese correo ya tiene cuenta: escribe tu contraseña actual.') : m);
      setEnviando(false);
    }
  };

  if (empresa === undefined)
    return (
      <Tarjeta>
        <p className="lead">Abriendo tu invitación…</p>
      </Tarjeta>
    );
  if (!empresa)
    return (
      <Tarjeta>
        <div>
          <h1>Esta invitación ya no sirve</h1>
          <p className="lead">Venció o ya se usó. Pídele a quien te invitó que la reenvíe desde Administración → Usuarios.</p>
        </div>
      </Tarjeta>
    );
  return (
    <Tarjeta>
      <div>
        <h1>Únete a {empresa.nombre}</h1>
        <p className="lead">
          {tieneCuenta
            ? 'Ya tienes cuenta en CRM XmarteK: entra con tu contraseña de siempre y quedarás también en esta empresa.'
            : 'Crea tu contraseña para entrar al CRM.'}
        </p>
      </div>
      <form className="form" onSubmit={enviar}>
        <label>
          Correo
          <input id="inv-email" type="email" value={v.email} onChange={(e) => setV({ ...v, email: e.target.value })} required autoComplete="username" readOnly={!!q.get('email')} />
        </label>
        {tieneCuenta ? null : (
        <div className="grid2">
          <label>
            Nombre
            <input id="inv-nombre" value={v.nombre} onChange={(e) => setV({ ...v, nombre: e.target.value })} autoComplete="given-name" autoFocus />
          </label>
          <label>
            Apellido
            <input id="inv-apellido" value={v.apellido} onChange={(e) => setV({ ...v, apellido: e.target.value })} autoComplete="family-name" />
          </label>
        </div>
        )}
        <label>
          {tieneCuenta ? 'Tu contraseña de CRM XmarteK' : 'Contraseña'}
          <input
            id="inv-clave"
            type="password"
            value={v.clave}
            onChange={(e) => setV({ ...v, clave: e.target.value })}
            required
            minLength={tieneCuenta ? undefined : CLAVE_MIN}
            autoComplete={tieneCuenta ? 'current-password' : 'new-password'}
            autoFocus={tieneCuenta}
          />
        </label>
        {error ? (
          <p className="error" role="alert">
            {error}
          </p>
        ) : null}
        <button className="btn primary lg" disabled={enviando || !v.email || !v.clave}>
          {enviando ? 'Entrando…' : 'Entrar al CRM'}
        </button>
      </form>
    </Tarjeta>
  );
}

export function NuevaClave() {
  const token = decodeURIComponent(window.location.pathname.split('/')[2] ?? '');
  const [email, setEmail] = useState<string | null | undefined>(undefined);
  const [clave, setClave] = useState('');
  const [repetir, setRepetir] = useState('');
  const [error, setError] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [listo, setListo] = useState(false);

  useEffect(() => {
    validarEnlaceClave(token).then(
      (r) => setEmail(r?.email ?? null),
      () => setEmail(null),
    );
  }, [token]);

  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    if (clave.length < CLAVE_MIN) return setError(`La contraseña debe tener al menos ${CLAVE_MIN} caracteres.`);
    if (clave !== repetir) return setError('Las dos contraseñas no coinciden.');
    setError('');
    setEnviando(true);
    try {
      await cambiarClaveConEnlace(token, clave);
      setSession(null);
      setListo(true);
    } catch (err) {
      setError((err as Error).message);
      setEnviando(false);
    }
  };

  if (email === undefined)
    return (
      <Tarjeta>
        <p className="lead">Revisando el enlace…</p>
      </Tarjeta>
    );
  if (!email)
    return (
      <Tarjeta>
        <div>
          <h1>Este enlace ya no sirve</h1>
          <p className="lead">Venció o ya se usó. En la pantalla de entrada pulsa “¿Olvidaste tu contraseña?” para pedir uno nuevo.</p>
        </div>
        <button className="btn primary lg" onClick={() => window.location.replace('/')}>
          Ir a la entrada
        </button>
      </Tarjeta>
    );
  if (listo)
    return (
      <Tarjeta>
        <div>
          <h1>Contraseña cambiada</h1>
          <p className="lead">Ya puedes entrar con tu contraseña nueva.</p>
        </div>
        <button className="btn primary lg" onClick={() => window.location.replace('/')}>
          Entrar
        </button>
      </Tarjeta>
    );
  return (
    <Tarjeta>
      <div>
        <h1>Nueva contraseña</h1>
        <p className="lead">{email}</p>
      </div>
      <form className="form" onSubmit={enviar}>
        <input type="email" value={email} autoComplete="username" readOnly hidden />
        <label>
          Contraseña nueva
          <input id="nc-clave" type="password" value={clave} onChange={(e) => setClave(e.target.value)} required minLength={CLAVE_MIN} autoComplete="new-password" autoFocus />
        </label>
        <label>
          Repítela
          <input id="nc-repetir" type="password" value={repetir} onChange={(e) => setRepetir(e.target.value)} required autoComplete="new-password" />
        </label>
        {error ? (
          <p className="error" role="alert">
            {error}
          </p>
        ) : null}
        <button className="btn primary lg" disabled={enviando}>
          {enviando ? 'Guardando…' : 'Guardar contraseña'}
        </button>
      </form>
    </Tarjeta>
  );
}
